import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DocumentTypeService from "../api/DocumentTypeService";
import CustomSnackbar from "../components/common/CustomSnackbar";
import { MDBContainer } from "mdb-react-ui-kit";
import FieldDefinitionService from "../api/FieldDefinitionsService";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const AdminDocumentTypesPage = () => {
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const isLoading = useSelector((state) => state.user.isLoading);
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [docTypeToEdit, setDocTypeToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [allDocTypes, setAllDocTypes] = useState([]);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      loadData();
    }
    if (!isLoading && !isLoggedIn) {
      navigate("/login");
    }
  }, [isLoading, isLoggedIn]);

  if (!isLoading && !isLoggedIn) {
    return <div>Loading...</div>;
  }

  // Ensure mainFields always contains ID 1
  const ensureMainFieldsHas1 = (arr) => Array.from(new Set([1, ...(arr || [])]));
  // Ensure fieldMap always contains key 1 with a valid order
  const ensureFieldMapHas1 = (map) => {
    const newMap = { ...(map || {}) };
    if (!(1 in newMap)) {
      const orders = Object.values(newMap).filter(v => typeof v === "number");
      const maxOrder = orders.length > 0 ? Math.max(...orders) : 0;
      newMap[1] = maxOrder + 1;
    }
    return newMap;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [catRes, fdRes] = await Promise.all([
        DocumentTypeService.getAll(),
        FieldDefinitionService.getAll()
      ]);
      const fixedCatRes = catRes.data.map(c => ({
        ...c,
        fieldMap: c.fieldMap || {}
      }));
      setCategories(fixedCatRes);
      setAllDocTypes(fdRes.data);
    } catch (e) {
      setSnackbar({ open: true, message: "Помилка: " + e.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (updated) => {
    const ensured = {
      ...updated,
      mainFields: ensureMainFieldsHas1(updated?.mainFields),
      fieldMap: ensureFieldMapHas1(updated?.fieldMap)
    };
    if (!ensured.name?.trim()) {
      setShowValidation(true);
      setSnackbar({ open: true, message: "Поле 'Назва' є обовʼязковим.", severity: "error" });
      return;
    }
    try {
      if (ensured.id) await DocumentTypeService.update(ensured);
      else await DocumentTypeService.create(ensured);
      setSnackbar({ open: true, message: "Збережено!", severity: "success" });
      setEditDialogOpen(false);
      loadData();
    } catch (e) {
      setSnackbar({ open: true, message: "Помилка збереження: " + e.message, severity: "error" });
    }
  };

  const promptDelete = (cat) => {
    setCategoryToDelete(cat);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await DocumentTypeService.deleteById(categoryToDelete.id);
      setSnackbar({ open: true, message: "Видалено!", severity: "success" });
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      setCategories((prev) => prev.filter((c) => c.id !== categoryToDelete.id));
    } catch (e) {
      setSnackbar({ open: true, message: "Помилка видалення: " + e.message, severity: "error" });
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "name", headerName: "Назва", flex: 1 },
    { field: "description", headerName: "Опис", flex: 2 },
    {
      field: "fieldMap",
      headerName: "Мапінг полів",
      flex: 2,
      valueGetter: (value) => JSON.stringify(value)
    },
    {
      field: "actions", type: "actions", headerName: "Дії", width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon/>} label="Редагувати"
          onClick={() => {
            const mainFields = params.row.mainFields || [];
            const ensuredMainFields = ensureMainFieldsHas1(mainFields);
            setDocTypeToEdit({
              ...params.row,
              fieldMap: ensureFieldMapHas1(params.row.fieldMap || {}),
              mainFields: ensuredMainFields
            });
            setEditDialogOpen(true);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon/>} label="Видалити"
          onClick={() => promptDelete(params.row)}
        />
      ]
    }
  ];

  return (
    <Box p={2} marginLeft={5} marginRight={5}>
      <MDBContainer className="d-flex justify-content-center align-items-center text-center">
        <Typography variant="h5" mb={2} marginRight={20}>Типи документів</Typography>
        <Button
          startIcon={<AddIcon/>} variant="contained"
          sx={{ mb: 2 }}
          onClick={() => {
            const documentNameField = allDocTypes.find(f => f.name === "document_name");
            const fieldMap = documentNameField ? { [documentNameField.id]: 1 } : {};
            const mainFields = documentNameField ? [documentNameField.id] : [];
            const ensuredMainFields = ensureMainFieldsHas1(mainFields);
            setDocTypeToEdit({
              name: "",
              description: "",
              orderNumber: categories.length + 1,
              fieldMap: ensureFieldMapHas1(fieldMap),
              mainFields: ensuredMainFields
            });
            setEditDialogOpen(true);
            setShowValidation(false);
          }}
        >
          Додати тип документу
        </Button>
      </MDBContainer>

      {loading ? <CircularProgress/> : (
        <DataGrid
          rows={categories}
          columns={columns}
          autoHeight
          pageSize={10}
          rowsPerPageOptions={[10]}
          getRowId={(row) => row.id}
          rowSelection={false}
          showToolbar={true}
        />
      )}

      {/* ✏️ Діалог створення / редагування */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{docTypeToEdit?.id ? "Редагування типу документа" : "Створення типу документа"}</DialogTitle>
        <DialogContent>
          <TextField
            required fullWidth margin="dense" label="Назва"
            value={docTypeToEdit?.name || ""}
            onChange={(e) => {
              setDocTypeToEdit((c) => ({ ...c, name: e.target.value }));
              setShowValidation(false);
            }}
            error={showValidation && !docTypeToEdit?.name?.trim()}
            helperText={showValidation && !docTypeToEdit?.name?.trim() ? "Назва обовʼязкова" : ""}
          />
          <TextField
            fullWidth margin="dense" label="Опис" multiline
            value={docTypeToEdit?.description || ""}
            onChange={(e) => setDocTypeToEdit((c) => ({ ...c, description: e.target.value }))}
          />

          {docTypeToEdit && (
            <>
              <Typography variant="subtitle1" mt={2} mb={1}>Призначити поля (4 TEXT, 1 DATE, 1 PRICE)</Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {[...allDocTypes]
                  .sort((a, b) => {
                    const orderA = docTypeToEdit.fieldMap?.[a.id] ?? Infinity;
                    const orderB = docTypeToEdit.fieldMap?.[b.id] ?? Infinity;
                    return orderA - orderB;
                  })
                  .map((dt) => {
                    const { id: fieldId } = dt;
                    const fieldMap = docTypeToEdit.fieldMap || {};
                    const mainFields = docTypeToEdit.mainFields || [];
                    const isChecked = fieldId in fieldMap;
                    const isMain = mainFields.includes(fieldId);
                    const selectedField = allDocTypes.find(f => f.id === fieldId);
                    const selectedFieldType = selectedField?.fieldType;
                    const systemName = selectedField?.systemName;
                    const isDocumentName = systemName === "document_name";
                    const typeCount = mainFields.reduce((acc, id) => {
                      const field = allDocTypes.find(f => f.id === id);
                      if (field?.fieldType) {
                        acc[field.fieldType] = (acc[field.fieldType] || 0) + 1;
                      }
                      return acc;
                    }, {});

                    const isDisabled = fieldId === 1 || (!isMain && (
                      (selectedFieldType === "TEXT" && (typeCount["TEXT"] || 0) >= 4) ||
                      (selectedFieldType === "PRICE" && (typeCount["PRICE"] || 0) >= 1) ||
                      (selectedFieldType === "DATE" && (typeCount["DATE"] || 0) >= 1) ||
                      (selectedFieldType === "COMMENT" && (typeCount["COMMENT"] || 0) >= 0)
                    )) || isDocumentName;

                    return (
                      <Box
                        key={dt.id}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                        py={1}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                const map = { ...docTypeToEdit.fieldMap };
                                if (e.target.checked) {
                                  const currentOrders = Object.values(map);
                                  const maxOrder = currentOrders.length > 0 ? Math.max(...currentOrders) : 0;
                                  map[dt.id] = maxOrder + 1;
                                } else {
                                  delete map[dt.id];
                                }
                                setDocTypeToEdit((c) => ({ ...c, fieldMap: ensureFieldMapHas1(map) }));
                              }}
                              disabled={systemName === "document_name" || fieldId === 1}
                            />
                          }
                          label={dt.name}
                        />
                        {isChecked && (
                          <Box display="flex" alignItems="center" justifyContent="flex-end" flexGrow={1} gap={2}>
                            <Typography variant="body2" color="textSecondary" marginRight={2}>
                              {selectedFieldType || "невідомо"}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isMain}
                                  onChange={(e) => {
                                    let newMainFields = [...(docTypeToEdit.mainFields || [])];
                                    if (e.target.checked) {
                                      if (!newMainFields.includes(dt.id)) newMainFields.push(dt.id);
                                    } else {
                                      newMainFields = newMainFields.filter(id => id !== dt.id);
                                    }
                                    newMainFields = ensureMainFieldsHas1(newMainFields);
                                    setDocTypeToEdit((c) => ({ ...c, mainFields: newMainFields }));
                                  }}
                                  disabled={isDisabled}
                                />
                              }
                              label="На головній"
                            />
                            <TextField
                              label="Порядок"
                              type="number"
                              size="small"
                              value={docTypeToEdit.fieldMap[dt.id]}
                              onChange={(e) => {
                                const map = { ...docTypeToEdit.fieldMap };
                                map[dt.id] = parseInt(e.target.value) || 1;
                                setDocTypeToEdit((c) => ({ ...c, fieldMap: map }));
                              }}
                              sx={{ width: 100 }}
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" color="success" onClick={() => handleSave(docTypeToEdit)}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🗑️ Підтвердження видалення */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Підтвердження видалення</DialogTitle>
        <DialogContent>
          <Typography>Впевнені, що хочете видалити <strong>{categoryToDelete?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Видалити</Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default AdminDocumentTypesPage;
